import { IncentiveCategory } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import IncentiveDisclosure from 'features/incentive/components/IncentiveDisclosure/IncentiveDisclosure';
import IncentiveProgramFallback from 'features/incentive/components/IncentiveProgramFallback/IncentiveProgramFallback';
import { formatPrizeDate } from 'features/incentive/helpers/formatPrizeDate';
import { getIncentiveCopyPrefix } from 'features/incentive/helpers/getIncentiveCopyPrefix';
import { IncentiveScreenNames } from 'features/incentive/helpers/incentiveAnalytics';
import { useIncentiveScreenViewed } from 'features/incentive/hooks/useIncentiveAnalytics';
import useIncentiveProgram from 'features/incentive/hooks/useIncentiveProgram';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSBoldText from 'shared/components/vxl/typography/primary/body/BodyXSBoldText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const SECTION_SEPARATOR_HEIGHT = 8;

interface IncentiveHowItWorksScreenProps {
  incentiveType: IncentiveCategory;
}

function IncentiveHowItWorksScreen({
  incentiveType,
}: IncentiveHowItWorksScreenProps) {
  const { programId } = useLocalSearchParams<{ programId: string }>();
  const { t: translatedText } = useTranslation('incentive', {
    keyPrefix: getIncentiveCopyPrefix(incentiveType, 'howItWorks'),
  });
  const { bottom } = useSafeAreaInsets();
  const programResult = useIncentiveProgram(incentiveType, programId);
  const { program, content } = programResult;

  useIncentiveScreenViewed({
    screenName: IncentiveScreenNames.HOW_IT_WORKS,
    isVisible: Boolean(program && content),
    incentiveType,
  });

  if (!program || !content) {
    return (
      <IncentiveProgramFallback
        incentiveType={incentiveType}
        programResult={programResult}
      />
    );
  }

  const sweepstakesContent = content.content;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={navigateBack} />
          ),
        }}
      />
      <ScrollView contentContainerStyle={{ paddingBottom: bottom }}>
        <View style={styles.section}>
          <TitleLgText role="heading" numberOfLines={0}>
            {translatedText('title')}
          </TitleLgText>
          <BodyBaseText numberOfLines={0}>
            {sweepstakesContent.intro}
          </BodyBaseText>
        </View>
        <View style={styles.section}>
          <TitleSmText role="heading" numberOfLines={0}>
            {sweepstakesContent.entry_tiers_title ??
              translatedText('entries.title')}
          </TitleSmText>
          <BodyBaseText numberOfLines={0}>
            {sweepstakesContent.entry_tiers_description ??
              translatedText('entries.body')}
          </BodyBaseText>
          <View style={styles.tableHeader}>
            <BodyXSBoldText numberOfLines={0}>
              {translatedText('entries.purchaseAmount')}
            </BodyXSBoldText>
            <BodyXSBoldText numberOfLines={0}>
              {translatedText('entries.chancesToWin')}
            </BodyXSBoldText>
          </View>
        </View>
        {sweepstakesContent.entry_tiers_display.map((tier) => (
          <ListItem
            key={`${tier.label}-${tier.chances}`}
            primaryCopy={tier.label}
            showsChevron={false}
            trailingAccessory={{
              id: `entry-tier-${tier.label}-${tier.chances}`,
              name: VXLComponentId.TAG,
              props: {
                text: tier.chances,
                type: TagType.INFO,
                iconKey: 'TICKET_FILLED',
              },
            }}
          />
        ))}
        <View style={styles.sectionSeparator} />
        <View style={styles.section}>
          <TitleSmText role="heading" numberOfLines={0}>
            {sweepstakesContent.prize_schedule_title ??
              translatedText('prizes.title')}
          </TitleSmText>
          <BodyBaseText numberOfLines={0}>
            {sweepstakesContent.prize_schedule_description ??
              translatedText('prizes.body', {
                startDate: formatPrizeDate(program.windowStart),
              })}
          </BodyBaseText>
        </View>
        {sweepstakesContent.prize_schedule.map((prize) => (
          <ListItem
            key={`${prize.date}-${prize.label ?? ''}-${prize.amount_display}-${prize.winner_count}`}
            primaryCopy={formatPrizeDate(prize.date)}
            secondaryCopy={prize.label}
            lineFormat={ListItemLineFormat.CAPTION}
            showsChevron={false}
            trailingAccessory={{
              id: `prize-${prize.date}-${prize.label ?? ''}-${prize.amount_display}-${prize.winner_count}`,
              name: VXLComponentId.LIST_ITEM_TEXT_STACK,
              props: {
                primaryCopy: prize.amount_display,
                secondaryCopy: translatedText('prizes.winnerCount', {
                  count: prize.winner_count,
                }),
              },
            }}
          />
        ))}
        <View style={styles.sectionSeparator} />
        <View style={styles.section}>
          <IncentiveDisclosure
            incentiveType={incentiveType}
            section="howItWorks"
            program={program}
          />
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  section: {
    gap: theme.spacing.s,
    padding: theme.spacing.m,
  },
  sectionSeparator: {
    backgroundColor: theme.color.background.surface,
    height: SECTION_SEPARATOR_HEIGHT,
  },
  tableHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: theme.spacing.s,
  },
});

export default IncentiveHowItWorksScreen;
